import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';

// project-imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { disconnectSocket, getSocket } from 'utils/socketConnect';

// types
import { AuthProps, JWTContextType } from 'types/auth';
import { KeyedObject } from 'types/root';

const chance = new Chance();

// ==============================|| ACTIONS ||============================== //
const LOGIN = 'LOGIN';
const LOGOUT = 'LOGOUT';

// ==============================|| REDUCER ||============================== //
type Action =
  | { type: typeof LOGIN; payload: Partial<AuthProps> }
  | { type: typeof LOGOUT; payload?: Partial<AuthProps> };

const authReducer = (state: AuthProps, action: Action): AuthProps => {
  switch (action.type) {
    case LOGIN:
      return {
        ...state,
        isLoggedIn: true,
        isInitialized: true,
        user: action.payload.user ?? state.user,
        token: action.payload.token ?? state.token
      };

    case LOGOUT:
      return {
        ...state,
        isLoggedIn: false,
        isInitialized: true,
        user: null,
        token: null
      };

    default:
      return state;
  }
};

// ==============================|| HELPERS ||============================== //
const verifyToken = (serviceToken: string | null): boolean => {
  if (!serviceToken) return false;
  const decoded: KeyedObject = jwtDecode(serviceToken);
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| INITIAL STATE ||============================== //
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  token: null
};

// ==============================|| CONTEXT ||============================== //
const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // init on mount
  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);
          const res = await axios.get('/user/verifyToken');
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              isInitialized: true,
              user: res?.data?.data,
              token: serviceToken
            }
          });
          if (res?.data?.data?._id) {
            getSocket(res?.data?.data?._id);
          }
        } else {
          dispatch({ type: LOGOUT, payload: { isInitialized: true } });
        }
      } catch (err) {
        console.error(err);
        dispatch({ type: LOGOUT, payload: { isInitialized: true } });
      }
    };

    init();
  }, []);

  // ==============================|| METHODS ||============================== //
  const login = async (email: string, password: string) => {
    const res = await axios.post('/user/login', { email, password });
    const token = res.data?.data?.loginToken;
    setSession(token);
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        isInitialized: true,
        user: res?.data?.data,
        token
      }
    });

    if (res?.data?.data?._id) {
      getSocket(res?.data?.data?._id);
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers!),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const fetchUser = async () => {
    const res = await axios.get('/user');

    if (res.status === 200) {
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          isInitialized: true,
          user: res?.data?.data?.userData,
          token: state.token
        }
      });
    }

    return res;
  };

  const updateProfile = async (reqData: object) => {
    const res = await axios.put('/user/', reqData);

    if (res.status === 200) {
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          isInitialized: true,
          user: res?.data?.data?.updatedUser,
          token: state.token
        }
      });
    }

    return res;
  };

  const logout = async () => {
    const res = await axios.post(`/user/logout/${state?.user?._id}`, {});
    if (res.status === 200) {
      setSession(null);
      dispatch({ type: LOGOUT, payload: { isInitialized: true } });
      disconnectSocket();
      window.location.pathname = '/login';
    }
  };

  const resetPassword = async (email: string) => { };

  // ==============================|| RENDER ||============================== //
  if (!state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider value={{ ...state, login, logout, register, resetPassword, updateProfile, fetchUser }}>
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
